import type { FormikHelpers } from 'formik';
import { DateTime as DT } from 'luxon';
import * as apiObject from 'api/apiObjects';
import type { HistoryDeadlineFormValues } from './types';

type TranslateFn = (key: string) => string;
type ShowToastFn = (message: string, type: 'success' | 'danger') => void;

export interface SubmitHistoryDeadlineParams {
    objectId: number;
    t: TranslateFn;
    showToast: ShowToastFn;
    onSuccess: (objectDeadline: number) => Promise<void>;
}

export const submitHistoryDeadline = async (
    values: HistoryDeadlineFormValues,
    helpers: FormikHelpers<HistoryDeadlineFormValues>,
    params: SubmitHistoryDeadlineParams,
): Promise<void> => {
    const { objectId, t, showToast, onSuccess } = params;
    const { setSubmitting, setFieldError } = helpers;

    setSubmitting(true);
    try {
        const iso = String(values.deadline || '');
        const deadline = iso ? DT.fromISO(iso, { zone: 'local' }).toMillis() : 0;
        if (!deadline) {
            setFieldError('deadline', t('historyDeadline.errDate'));
            return;
        }

        const comment = String(values.comment || '').trim();
        if (!comment) {
            setFieldError('comment', t('historyDeadline.errComment'));
            return;
        }

        const res = await apiObject.setObjectDeadline(objectId, {
            deadline,
            comment,
        });

        if (res?.stat) {
            await onSuccess(Number(res.objectDeadline ?? deadline) || deadline);
            showToast(t('historyDeadline.messChanged'), 'success');
            return;
        }

        if (res?.errCode === 'DEADLINE_UNCHANGED') {
            setFieldError('deadline', t('historyDeadline.errUnchanged'));
            return;
        }

        if (res?.errCode === 'COMMENT_REQUIRED') {
            setFieldError('comment', t('historyDeadline.errComment'));
            return;
        }

        if (res?.errCode === 'OBJECT_LOCKED') {
            showToast(t('historyDeadline.errLocked'), 'danger');
            return;
        }

        showToast(t('common:messError'), 'danger');
    } finally {
        setSubmitting(false);
    }
};
